import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { MenuMobile, Menu, CancelCircleFilled, Search, EasyLanguage, SignLanguage } from '@aok/icon-functional'

import defaultProps from '../../../utils/propTypes/defaultProps'

import LogoBrandWhite from '../../../images/logo-brand-white.svg'
import LogoBrandGreen from '../../../images/logo-brand-green.svg'
import LogoSmallWhite from '../../../images/logo-small-white.svg'
import LogoSmallGreen from '../../../images/logo-small-green.svg'

import Comment from '../../atoms/Comment/Comment'
import SkipLink from '../../atoms/SkipLink/SkipLink'

import FlyoutContainer from './FlyoutContainer'
import DesktopSearch from './DesktopSearch'
import DesktopMenu from './DesktopMenu'
import MobileMenu from './MobileMenu'

import { useWindowSize } from '../../../hooks/useWindowSize'
import { BREAKPOINT_840 } from '../../../utils/constants/breakpoints'

const MagazineHeader = ({
  fixedLinks,
  headerRef,
  mainMenu,
  tags,
  hideHeaderShadow,
  hasScrollIndicator,
  mainContentRef,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [isAnyFlyoutOpen, setIsAnyFlyoutOpen] = useState(false)
  const [isSticky, setIsSticky] = useState(false)
  const [windowWidth] = useWindowSize()
  const scrollIndicatorRef = useRef(null)

  let previousActiveElement

  const closeFlyout = (toggle) => {
    previousActiveElement && previousActiveElement.focus()
    toggle(false)
  }

  const openFlyout = (toggle) => {
    previousActiveElement = document.activeElement
    toggle(true)
  }

  const toggleMenu = () => {
    closeFlyout(setIsSearchOpen)

    if (isMenuOpen) {
      closeFlyout(setIsMenuOpen)
    } else {
      openFlyout(setIsMenuOpen)
    }
  }

  const toggleSearch = () => {
    closeFlyout(setIsMenuOpen)

    if (isSearchOpen) {
      closeFlyout(setIsSearchOpen)
    } else {
      openFlyout(setIsSearchOpen)
    }
  }

  useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      setIsAnyFlyoutOpen(true)
    } else {
      setIsAnyFlyoutOpen(false)
    }
  }, [isMenuOpen, isSearchOpen])

  useEffect(() => {
    const checkHeader = () => {
      const offset = document.documentElement.scrollTop

      if (offset > 0) {
        setIsSticky(true)
      } else {
        setIsSticky(false)
      }
    }

    window.addEventListener('scroll', checkHeader)
    checkHeader()

    return () => {
      window.removeEventListener('scroll', checkHeader)
    }
  }, [])

  useEffect(() => {
    const updateScrollIndicator = () => {
      const dom = document.documentElement
      const mainContent = !mainContentRef ? dom : mainContentRef.current

      const scrollTop = document.body.scrollTop || dom.scrollTop
      const height = mainContent.scrollHeight - dom.clientHeight + headerRef.current.offsetHeight
      const scrolled = (scrollTop / height) * 100
      scrollIndicatorRef.current.style.width = `${scrolled}%`
    }

    if (hasScrollIndicator) {
      window.addEventListener('scroll', updateScrollIndicator)
    }

    return () => {
      window.removeEventListener('scroll', updateScrollIndicator)
    }
  }, [hasScrollIndicator, mainContentRef, headerRef])

  return (
    <div data-tracking-name="magazine-header">
      <header
        className={`
          ${isSticky && !isAnyFlyoutOpen ? '1000:h-72' : '1000:h-88 1280:h-96'}
          ${!isSticky && !isAnyFlyoutOpen ? 'bg-005e3f' : 'bg-ffffff'}
          ${!isSticky && hideHeaderShadow ? '' : 'shadow-header'}
          fixed z-30 w-full h-64 600:h-80
          transition-all duration-350 ease-in
        `}
        ref={headerRef}
      >
        <Comment text="googleoff: all" />
        <SkipLink id="main-content">Zum Hauptinhalt springen</SkipLink>

        {hasScrollIndicator && (
          <>
            <div className={`absolute bottom-0 left-0 right-0 ${isAnyFlyoutOpen ? 'hidden' : ''}`}>
              <div className="w-full h-4 bg-dfe3e6" />
            </div>
            <div className={`absolute bottom-0 left-0 right-0 ${isAnyFlyoutOpen ? 'hidden' : ''}`}>
              <div ref={scrollIndicatorRef} className="w-0 h-4 bg-005e3f" />
            </div>
          </>
        )}

        <div
          className="
            h-full w-full flex items-center
            relative
            px-16 720:px-24 1000:px-32
            1280:px-0 1280:w-1216 1280:mx-auto
          "
        >
          <a
            href="/pk/magazin/"
            title="Startseite AOK"
            className={`
              flex flex-shrink-0
              transition-margin duration-350 ease-in
              ${isSticky ? '-ml-68 840:-ml-86' : 'ml-0'}
            `}
          >
            {/* Logo */}
            <img
              src={isSticky || isAnyFlyoutOpen ? LogoBrandGreen : LogoBrandWhite}
              alt="AOK Wortmarke"
              className={`
                block w-auto h-32 840:h-40
                transition-all duration-350 ease-in
                ${isSticky ? 'opacity-0' : 'opacity-100 1280:h-48'}
              `}
            />
            <img
              src={isSticky || isAnyFlyoutOpen ? LogoSmallGreen : LogoSmallWhite}
              alt="AOK Lebensbaum"
              className={`
                block w-auto h-32 840:h-40
                transition-all duration-350 ease-in
                ${isSticky ? '' : '1280:h-48'}
              `}
            />
          </a>

          <div className={`ml-8 360:ml-16 840:ml-24 flex-1 truncate ${!isSticky ? '1280:ml-40' : ''}`}>
            <div
              className={`
                block truncate
                text-14 leading-18 font-semibold
                600:text-16 600:leading-24
                ${!isSticky && !isAnyFlyoutOpen ? 'text-ffffff' : ''}
              `}
            >
              Gesundheitsmagazin
            </div>
          </div>

          {/* Accessibility links */}
          <div className="hidden 840:grid 840:gap-24 840:grid-flow-col">
            {/* Leichte Sprache */}
            <a
              href={fixedLinks?.easyLanguage?.url}
              title={fixedLinks?.easyLanguage?.title}
              className={`
                group flex flex-col items-center
                ${!isSticky && !isAnyFlyoutOpen ? 'hover:text-50eb5d focus:text-50eb5d text-ffffff' : 'text-6d767c'}
              `}
            >
              <EasyLanguage className="fill-current h-20 w-20 transition-transform duration-350 ease-in group-hover:scale-120" />
            </a>

            {/* Gebärdensprache */}
            <a
              href={fixedLinks?.signLanguage?.url}
              title={fixedLinks?.signLanguage?.title}
              className={`
                group flex flex-col items-center
                ${!isSticky && !isAnyFlyoutOpen ? 'hover:text-50eb5d focus:text-50eb5d text-ffffff' : 'text-6d767c'}
              `}
            >
              <SignLanguage className="fill-current h-20 w-20 transition-transform duration-350 ease-in group-hover:scale-120" />
            </a>
          </div>

          {/* Meta Navigation */}
          <div
            className={`
              transition-all duration-350 ease-in
              840:grid 840:grid-flow-col 840:gap-24
              840:border-l-2
              1280:pl-32 1280:ml-32
              ${!isSticky && !isAnyFlyoutOpen ? '840:border-4d7e6e text-ffffff' : '840:border-dfe3e6 text-6d767c'}
              ${isSticky ? '840:pl-32 840:ml-32' : '840:pl-24 840:ml-24 1280:gap-32'}
            `}
          >
            {/* Search */}
            <button
              type="button"
              onClick={toggleSearch}
              aria-label="Suchen"
              className={`
                group hidden 840:inline
                transition-padding duration-350 ease-in
                ${isSearchOpen ? 'text-005e3f' : ''}
                ${!isSticky && !isAnyFlyoutOpen ? 'hover:text-50eb5d focus-visible:text-50eb5d' : ''}
              `}
            >
              <Search
                className={`fill-current w-20 h-20 inline transition-transform duration-350 ease-in group-hover:scale-120`}
              />

              <span
                className={`
                  hidden ${isSticky ? '' : '840:inline'}
                  text-16 leading-24 whitespace-nowrap
                  ml-8
                `}
              >
                Suche
              </span>
            </button>

            {/* MenuToggle */}
            <button
              type="button"
              onClick={toggleMenu}
              aria-label="Navigation öffnen"
              className={`
                group
                transition-padding duration-350 ease-in
                ${isMenuOpen ? 'text-005e3f' : ''}
                ${!isSticky && !isAnyFlyoutOpen ? 'hover:text-50eb5d focus-visible:text-50eb5d' : ''}
              `}
            >
              {isMenuOpen && windowWidth < BREAKPOINT_840 ? (
                <CancelCircleFilled className="fill-current w-24 h-24 840:w-20 840:h-20 inline" />
              ) : (
                <>
                  <MenuMobile className="fill-current w-24 h-24 inline 840:hidden" />
                  <Menu className="fill-current w-20 h-20 hidden 840:inline transition-transform duration-350 ease-in group-hover:scale-120" />
                </>
              )}

              <span
                className={`
                  hidden ${isSticky ? '' : '840:inline'}
                  text-16 leading-24 whitespace-nowrap
                  ml-8
                `}
              >
                Alle Themen
              </span>
            </button>
          </div>
        </div>

        {/* mobile menu */}
        <FlyoutContainer className={`${isMenuOpen || isSearchOpen ? 'block 840:hidden' : 'hidden'}`}>
          <MobileMenu
            mainMenu={mainMenu}
            fixedLinks={fixedLinks}
            tags={tags}
            onSubmitSearch={() => setIsSearchOpen(false)}
          />
        </FlyoutContainer>

        {/* all topics (desktop) */}
        <FlyoutContainer className={`${isMenuOpen ? 'hidden 840:block' : 'hidden'}`}>
          <DesktopMenu mainMenu={mainMenu} fixedLinks={fixedLinks} tags={tags} onCloseButtonClick={toggleMenu} />
        </FlyoutContainer>

        {/* search (desktop) */}
        <FlyoutContainer className={`${isSearchOpen ? 'hidden 840:block' : 'hidden'}`}>
          <DesktopSearch
            setIsSearchOpen={setIsSearchOpen}
            onSubmitSearch={() => setIsSearchOpen(false)}
            onCloseButtonClick={toggleSearch}
          />
        </FlyoutContainer>

        <Comment text="googleon: all" />
      </header>
    </div>
  )
}

MagazineHeader.propTypes = {
  headerRef: PropTypes.shape({
    current: PropTypes.object,
  }),
  mainMenu: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  tags: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  fixedLinks: PropTypes.shape({
    easyLanguage: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
    signLanguage: PropTypes.shape({
      title: PropTypes.string,
      url: PropTypes.string,
    }),
  }),
  hideHeaderShadow: PropTypes.bool,
  hasScrollIndicator: PropTypes.bool,
}

MagazineHeader.defaultProps = {
  headerRef: null,
  mainMenu: [
    {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
    {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
    {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
    {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
  ],
  tags: [
    {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
  ],
  fixedLinks: {
    easyLanguage: {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
    signLanguage: {
      title: defaultProps.TITLE,
      url: defaultProps.URL,
    },
  },
  hasScrollIndicator: false,
}

export default MagazineHeader
